import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';

const STATUS_OPTIONS = [
    { value: 1, label: 'قيد المراجعة', tone: 'warning' },
    { value: 2, label: 'معتمد', tone: 'success' },
    { value: 3, label: 'مرفوض', tone: 'danger' },
    { value: 4, label: 'مطلوب تعديل', tone: 'info' },
];

const ACTION_OPTIONS = {
    APPROVE: 'Approve',
    PARTIAL: 'PartialApprove',
    REJECT: 'Reject',
    MODIFY: 'RequestModification',
};

export default {
    name: 'PharmacyDashboard',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage(
                'error',
                'غير مصرح',
                'ليس لديك صلاحية للوصول إلى لوحة مراجعة الصيدلة'
            );
            this.href('/Home');
            return;
        }
        await this.loadBranches();
        await this.GetStatistics();
        this.GetInfo(1);
    },

    computed: {
        canViewPage() {
            if (!this.loginDetails) return false;
            if (this.isMinistryPharmacyDepartmentRole()) return true;
            if (this.isAdministratorRole()) return true;
            if (this.hasInventoryAndStockAccessUi()) return true;
            return false;
        },
        canReview() {
            return this.canReviewMedicineOrdersUi();
        },
        statsCards() {
            const s = this.Statistics || {};
            return [
                this.statCard('طلبات قيد المراجعة', s.pending, 'warning', 'ti ti-clock'),
                this.statCard('طلبات تمت مراجعتها اليوم', s.reviewedToday, 'info', 'ti ti-calendar'),
                this.statCard('موافقات جزئية', s.partialApprovals, 'primary', 'ti ti-adjustments'),
                this.statCard('طلبات معلقة (> 3 أيام)', s.stale, 'danger', 'ti ti-alert-triangle'),
            ];
        },
        statusOptions() { return STATUS_OPTIONS; },
        statusBadgeClass() {
            return (status) => {
                const opt = STATUS_OPTIONS.find(x => x.value === Number(status));
                return opt ? `badge bg-label-${opt.tone}` : 'badge bg-label-secondary';
            };
        },
        statusLabelOf() {
            return (status) => {
                const opt = STATUS_OPTIONS.find(x => x.value === Number(status));
                return opt ? opt.label : '—';
            };
        },
        daysBadgeClass() {
            return (item) => {
                if (item && item.isStale) return 'pharmacy-days-badge is-stale';
                if (item && Number(item.daysWaiting) >= 2) return 'pharmacy-days-badge is-warning';
                return 'pharmacy-days-badge';
            };
        },
        modalLinesApprovedTotal() {
            return (this.modalLines || []).reduce(
                (s, l) => s + (Number(l.QuantityApproved) || 0),
                0
            );
        },
        modalLinesRequestedTotal() {
            return (this.modalLines || []).reduce(
                (s, l) => s + (Number(l.QuantityRequested) || 0),
                0
            );
        },
        modalIsModificationActionDisabled() {
            return !this.modalNote || this.modalNote.trim().length === 0;
        },
    },

    data() {
        return {
            ScreenTitle: 'لوحة مراجعة الصيدلة',
            pageNo: 1,
            pageSize: 10,
            pages: 0,

            FilterFacility: '',
            FilterStatus: '',
            FilterFrom: '',
            FilterTo: '',
            Search: '',

            Info: [],
            Statistics: {},

            branchesList: [],

            modalOrder: null,
            modalLines: [],
            modalNote: '',
            modalReviewing: false,

            currentDetail: null,
            detailLoading: false,
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
        momentTime(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'; },
    },

    methods: {
        tableRowNumber(i) {
            return ((Number(this.pageNo) - 1) * Number(this.pageSize)) + i + 1;
        },

        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string'
                    ? err.response.data
                    : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch {
                this.branchesList = [];
            }
        },

        async GetInfo(pageNo) {
            this.pageNo = pageNo || 1;
            this.$blockUI.Start();
            try {
                const res = await this.$http.GetPharmacyOrders(
                    this.pageNo,
                    this.pageSize,
                    this.Search,
                    this.FilterStatus,
                    this.FilterFacility,
                    this.FilterFrom,
                    this.FilterTo
                );
                this.Info = (res && res.data && res.data.info) || [];
                const count = (res && res.data && res.data.count) || 0;
                this.pages = Math.ceil(count / this.pageSize);
            } catch (err) {
                this.Info = [];
                this.pages = 0;
                this.$helper.ShowMessage('error', 'تعذّر تحميل الطلبات', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
            }
        },

        async GetStatistics() {
            try {
                const res = await this.$http.GetPharmacyStatistics(this.FilterFacility);
                this.Statistics = (res && res.data) || {};
            } catch {
                this.Statistics = {};
            }
        },

        ApplyFilters() {
            this.GetStatistics();
            this.GetInfo(1);
        },

        ResetFilters() {
            this.FilterFacility = '';
            this.FilterStatus = '';
            this.FilterFrom = '';
            this.FilterTo = '';
            this.Search = '';
            this.ApplyFilters();
        },

        async OpenReview(item) {
            this.modalOrder = null;
            this.modalLines = [];
            this.modalNote = '';
            this.$blockUI.Start();
            try {
                const res = await this.$http.GetPharmacyOrder(item.id);
                const head = (res && res.data && res.data.info) || null;
                if (!head) throw new Error('تعذّر تحميل الطلب');
                this.modalOrder = head;
                this.modalLines = (head.lines || []).map(l => ({
                    Id: l.id,
                    ProductsId: l.productsId,
                    ProductName: l.productName,
                    BatchNumber: l.batchNumber,
                    QuantityRequested: Number(l.quantityRequested) || 0,
                    QuantityApproved:
                        l.quantityApproved == null
                            ? Number(l.quantityRequested) || 0
                            : Number(l.quantityApproved) || 0,
                    LineNote: '',
                }));
                this.modalNote = head.reviewNote || '';
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر فتح المراجعة', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
            }
        },

        modalLineSetMax(line) {
            if (!line) return;
            line.QuantityApproved = Number(line.QuantityRequested) || 0;
        },

        modalLineSetZero(line) {
            if (!line) return;
            line.QuantityApproved = 0;
        },

        async OpenDetail(item) {
            this.currentDetail = null;
            this.detailLoading = true;
            try {
                const res = await this.$http.GetPharmacyOrder(item.id);
                this.currentDetail = (res && res.data && res.data.info) || null;
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر تحميل التفاصيل', this.extractError(err));
            } finally {
                this.detailLoading = false;
            }
        },

        async submitReview(action) {
            if (!this.modalOrder) return;

            if (action === ACTION_OPTIONS.MODIFY && this.modalIsModificationActionDisabled) {
                this.$helper.ShowMessage('warning', 'ملاحظة مطلوبة', 'الرجاء إدخال ملاحظة طلب التعديل.');
                return;
            }

            if (action === ACTION_OPTIONS.PARTIAL) {
                if (!Array.isArray(this.modalLines) || this.modalLines.length === 0) return;
                const allZero = this.modalLines.every(l => (Number(l.QuantityApproved) || 0) === 0);
                if (allZero) {
                    this.$helper.ShowMessage(
                        'warning',
                        'كميات صفرية',
                        'لا يمكن اعتماد طلب بكميات صفرية لكل الأسطر — استخدم الرفض بدلاً.'
                    );
                    return;
                }
            }

            const titleByAction = {
                [ACTION_OPTIONS.APPROVE]: 'تأكيد الموافقة الكاملة',
                [ACTION_OPTIONS.PARTIAL]: 'تأكيد الموافقة الجزئية',
                [ACTION_OPTIONS.REJECT]: 'تأكيد الرفض',
                [ACTION_OPTIONS.MODIFY]: 'تأكيد طلب التعديل',
            };
            const textByAction = {
                [ACTION_OPTIONS.APPROVE]: 'سيتم اعتماد الطلب بكامل الكميات المطلوبة.',
                [ACTION_OPTIONS.PARTIAL]: 'سيتم اعتماد الكميات المعدّلة المعروضة.',
                [ACTION_OPTIONS.REJECT]: 'سيتم رفض الطلب — تأكد من إدخال السبب في حقل الملاحظات.',
                [ACTION_OPTIONS.MODIFY]: 'سيتم إعادة الطلب إلى المرفق لطلب تعديل.',
            };

            const ok = await Swal.fire({
                icon: 'question',
                title: titleByAction[action] || 'تأكيد المراجعة',
                text: textByAction[action] || '',
                showCancelButton: true,
                confirmButtonText: 'تأكيد',
                cancelButtonText: 'إلغاء',
            });
            if (!ok.isConfirmed) return;

            const body = {
                Action: action,
                ReviewNote: this.modalNote || '',
                Lines:
                    action === ACTION_OPTIONS.PARTIAL
                        ? this.modalLines.map(l => ({
                              LineId: l.Id,
                              QuantityApproved: Number(l.QuantityApproved) || 0,
                              LineNote: l.LineNote || '',
                          }))
                        : [],
            };

            this.modalReviewing = true;
            this.$blockUI.Start();
            try {
                const res = await this.$http.ReviewPharmacyOrder(this.modalOrder.id, body);
                const msg = (res && res.data) || 'تمت العملية بنجاح';
                this.$helper.ShowMessage('success', 'تمت العملية', msg);
                const closeBtn = document.querySelector('#PharmacyReviewModal [data-bs-dismiss="modal"]');
                if (closeBtn) closeBtn.click();
                this.modalOrder = null;
                this.modalLines = [];
                this.modalNote = '';
                await this.GetStatistics();
                await this.GetInfo(this.pageNo);
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر تنفيذ المراجعة', this.extractError(err));
            } finally {
                this.modalReviewing = false;
                this.$blockUI.Stop();
            }
        },

        approveAll() {
            return this.submitReview(ACTION_OPTIONS.APPROVE);
        },

        approvePartial() {
            return this.submitReview(ACTION_OPTIONS.PARTIAL);
        },

        rejectOrder() {
            return this.submitReview(ACTION_OPTIONS.REJECT);
        },

        requestModification() {
            return this.submitReview(ACTION_OPTIONS.MODIFY);
        },
    },
};
